const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export interface ChecklistItem {
  _id: string;
  task: string;
  kids: string[];
  dayType: number[];  // Array of 1-7 (1=Monday, 2=Tuesday, ..., 7=Sunday)
  order: number;
  isAdhoc?: boolean;  // true for one-time tasks
  adhocDate?: string | null;  // YYYY-MM-DD format, required when isAdhoc is true
}

export interface CompletionsMap {
  [key: string]: boolean;
}

// ETag storage for conditional requests
const etagStore: Map<string, string> = new Map();

/**
 * Fetch with ETag support for conditional requests
 * Returns null if server returns 304 (Not Modified)
 */
async function fetchWithETag<T>(url: string): Promise<{ data: T; notModified: false } | { data: null; notModified: true }> {
  const storedETag = etagStore.get(url);
  const headers: HeadersInit = {};

  if (storedETag) {
    headers['If-None-Match'] = storedETag;
  }

  const response = await fetch(url, { headers });

  if (response.status === 304) {
    return { data: null, notModified: true };
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }

  // Store the new ETag
  const newETag = response.headers.get('ETag');
  if (newETag) {
    etagStore.set(url, newETag);
  }

  const data = await response.json();
  return { data, notModified: false };
}

/**
 * Clear stored ETag for a specific URL (useful after mutations)
 */
export function clearETag(url: string): void {
  etagStore.delete(url);
}

/**
 * Clear all stored ETags
 */
export function clearAllETags(): void {
  etagStore.clear();
}

// Checklist Items API
export const checklistItemsAPI = {
  // Get all checklist items
  getAll: async (): Promise<ChecklistItem[]> => {
    const response = await fetch(`${API_URL}/checklist-items`);
    if (!response.ok) throw new Error('Failed to fetch checklist items');
    return response.json();
  },

  // Get checklist items by day number (1-7, where 1=Monday, 7=Sunday)
  // Optional date param (YYYY-MM-DD) to also include ad-hoc tasks for that date
  getByDayType: async (dayNumber: number, date?: string): Promise<ChecklistItem[]> => {
    const url = date
      ? `${API_URL}/checklist-items/by-day-type/${dayNumber}?date=${date}`
      : `${API_URL}/checklist-items/by-day-type/${dayNumber}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch checklist items');
    return response.json();
  },

  // Initialize default checklist items
  initDefaults: async () => {
    const response = await fetch(`${API_URL}/checklist-items/init-defaults`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to initialize defaults');
    return response.json();
  },

  // Create a new checklist item
  create: async (item: Omit<ChecklistItem, '_id'>): Promise<ChecklistItem> => {
    const response = await fetch(`${API_URL}/checklist-items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    if (!response.ok) throw new Error('Failed to create checklist item');
    return response.json();
  },

  // Update a checklist item
  update: async (id: string, item: Partial<ChecklistItem>): Promise<ChecklistItem> => {
    const response = await fetch(`${API_URL}/checklist-items/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item)
    });
    if (!response.ok) throw new Error('Failed to update checklist item');
    return response.json();
  },

  // Delete a checklist item
  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_URL}/checklist-items/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete checklist item');
  }
};

// Completions API
export const completionsAPI = {
  // Get completions for a specific date
  getByDate: async (dateKey: string): Promise<CompletionsMap> => {
    const response = await fetch(`${API_URL}/completions/${dateKey}`);
    if (!response.ok) throw new Error('Failed to fetch completions');
    return response.json();
  },

  // Toggle completion status
  toggle: async (dateKey: string, kidName: string, checklistItemId: string): Promise<{ completed: boolean }> => {
    const response = await fetch(`${API_URL}/completions/toggle`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dateKey, kidName, checklistItemId })
    });
    if (!response.ok) throw new Error('Failed to toggle completion');
    return response.json();
  },

  // Bulk get completions for multiple dates
  getBulk: async (dateKeys: string[]): Promise<{ [dateKey: string]: CompletionsMap }> => {
    const response = await fetch(`${API_URL}/completions/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dateKeys })
    });
    if (!response.ok) throw new Error('Failed to fetch bulk completions');
    return response.json();
  },

  // Get total star counts (all-time completions) per kid
  getTotalStars: async (): Promise<{ [kidName: string]: number }> => {
    const response = await fetch(`${API_URL}/completions/total-stars`);
    if (!response.ok) throw new Error('Failed to fetch total stars');
    return response.json();
  }
};

// Rewards & Redemptions
export interface Reward {
  _id: string;
  name: string;
  emoji: string;
  cost: number;
  order: number;
  active: boolean;
}

export interface StarBalance {
  stars: number;
  totalEarned: number;
  totalSpent: number;
}

export interface Redemption {
  _id: string;
  kidName: string;
  rewardId: string;
  rewardName: string;
  starsCost: number;
  redeemedAt: string;
}

export interface RedeemResponse {
  redemption: Redemption;
  stars: number;
  totalEarned: number;
  totalSpent: number;
}

export const rewardsAPI = {
  getAll: async (): Promise<Reward[]> => {
    const response = await fetch(`${API_URL}/rewards`);
    if (!response.ok) throw new Error('Failed to fetch rewards');
    return response.json();
  },

  create: async (reward: Omit<Reward, '_id' | 'active'>): Promise<Reward> => {
    const response = await fetch(`${API_URL}/rewards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reward)
    });
    if (!response.ok) throw new Error('Failed to create reward');
    return response.json();
  },

  update: async (id: string, reward: Partial<Reward>): Promise<Reward> => {
    const response = await fetch(`${API_URL}/rewards/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reward)
    });
    if (!response.ok) throw new Error('Failed to update reward');
    return response.json();
  },

  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_URL}/rewards/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete reward');
  },

  initDefaults: async () => {
    const response = await fetch(`${API_URL}/rewards/init-defaults`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error('Failed to initialize default rewards');
    return response.json();
  }
};

export const redemptionsAPI = {
  getStars: async (kidName: string): Promise<StarBalance> => {
    const response = await fetch(`${API_URL}/redemptions/stars/${kidName}`);
    if (!response.ok) throw new Error('Failed to fetch star balance');
    return response.json();
  },

  redeem: async (kidName: string, rewardId: string): Promise<RedeemResponse> => {
    const response = await fetch(`${API_URL}/redemptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kidName, rewardId })
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to redeem reward');
    }
    return response.json();
  },

  getHistory: async (kidName: string): Promise<Redemption[]> => {
    const response = await fetch(`${API_URL}/redemptions/${kidName}`);
    if (!response.ok) throw new Error('Failed to fetch redemption history');
    return response.json();
  }
};

// Punishments (time-boxed restrictions shown on a kid's checklist column)
export interface Punishment {
  _id: string;
  kidName: string;
  description: string;
  emoji: string;
  startDate: string;  // YYYY-MM-DD, inclusive
  endDate: string;    // YYYY-MM-DD, inclusive
  active: boolean;
}

export const punishmentsAPI = {
  // Active punishments in effect on a given date (YYYY-MM-DD)
  getForDate: async (dateKey: string): Promise<Punishment[]> => {
    const response = await fetch(`${API_URL}/punishments?date=${dateKey}`);
    if (!response.ok) throw new Error('Failed to fetch punishments');
    return response.json();
  },

  getAll: async (): Promise<Punishment[]> => {
    const response = await fetch(`${API_URL}/punishments`);
    if (!response.ok) throw new Error('Failed to fetch punishments');
    return response.json();
  },

  create: async (punishment: Omit<Punishment, '_id' | 'active' | 'emoji'> & { emoji?: string }): Promise<Punishment> => {
    const response = await fetch(`${API_URL}/punishments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(punishment)
    });
    if (!response.ok) throw new Error('Failed to create punishment');
    return response.json();
  },

  update: async (id: string, punishment: Partial<Punishment>): Promise<Punishment> => {
    const response = await fetch(`${API_URL}/punishments/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(punishment)
    });
    if (!response.ok) throw new Error('Failed to update punishment');
    return response.json();
  },

  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_URL}/punishments/${id}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete punishment');
  }
};

// Health check
export const healthCheck = async (): Promise<{ status: string; mongodb: string }> => {
  const response = await fetch(`${API_URL}/health`);
  if (!response.ok) throw new Error('Health check failed');
  return response.json();
};

// Polling-specific API methods with ETag support
// Returns null when data hasn't changed (304 response)
export const pollingAPI = {
  // Poll completions for a specific date (returns null if not modified)
  pollCompletions: async (dateKey: string): Promise<CompletionsMap | null> => {
    const url = `${API_URL}/completions/${dateKey}`;
    const result = await fetchWithETag<CompletionsMap>(url);
    return result.notModified ? null : result.data;
  },

  // Poll checklist items by day type (returns null if not modified)
  pollChecklistItems: async (dayNumber: number): Promise<ChecklistItem[] | null> => {
    const url = `${API_URL}/checklist-items/by-day-type/${dayNumber}`;
    const result = await fetchWithETag<ChecklistItem[]>(url);
    return result.notModified ? null : result.data;
  },

  // Invalidate cached ETags for completions (call after toggle)
  invalidateCompletions: (dateKey: string): void => {
    clearETag(`${API_URL}/completions/${dateKey}`);
  }
};
